/* DVY cart drawer: opens on add-to-cart, updates lines through the Ajax Cart API and
   re-renders itself with the Section Rendering API (section id "dvy-cart-drawer"). */

const DVY_CART_SECTION = 'dvy-cart-drawer';

class DvyCartDrawer extends HTMLElement {
  connectedCallback() {
    this.root = window.Shopify?.routes?.root || '/';
    this.lastTrigger = null;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-dvy-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.lastTrigger = opener;
      this.open();
    });

    document.addEventListener('dvy:cart:added', async (event) => {
      this.lastTrigger = event.detail?.source?.querySelector?.('[data-dvy-submit]') || document.activeElement;
      await this.refresh();
      this.open();
    });

    // Keep in sync with Horizon components that use Shopify's standard cart events (e.g. quick add).
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          Promise.resolve(event.promise)
            .catch(() => {})
            .then(() => this.refresh())
            .then(() => {
              if (event.action === 'add') this.open();
            });
        });
      })
      .catch(() => {});

    this.addEventListener('click', (event) => this.onClick(event));
    this.bindDialog();
    this.syncCount();
  }

  get dialog() {
    return this.querySelector('[data-dvy-cart-dialog]');
  }

  bindDialog() {
    const dialog = this.dialog;
    if (!dialog || dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    });
  }

  open() {
    const dialog = this.dialog;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  async onClick(event) {
    if (event.target.closest('[data-dvy-cart-close]')) {
      this.dialog.close();
      return;
    }
    const lineButton = event.target.closest('[data-dvy-line]');
    if (lineButton) {
      await this.update('change.js', { id: lineButton.dataset.dvyLine, quantity: Number(lineButton.dataset.dvyQty) }, lineButton);
      return;
    }
    const addButton = event.target.closest('[data-dvy-add]');
    if (addButton) {
      // One-time add: no selling_plan is sent (?appstle=true stops Appstle injecting one).
      await this.update('add.js?appstle=true', { items: [{ id: Number(addButton.dataset.dvyAdd), quantity: 1 }] }, addButton);
    }
  }

  async update(endpoint, body, trigger) {
    // Remember which control was used so focus can return to its re-rendered twin.
    this.focusIndex = [...this.querySelectorAll('[data-dvy-line], [data-dvy-add]')].indexOf(trigger);
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.root}cart/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: DVY_CART_SECTION }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message);
      const html = result.sections?.[DVY_CART_SECTION];
      if (html) this.render(html);
      else await this.refresh();
    } catch (error) {
      const errorEl = this.querySelector('[data-dvy-cart-error]');
      if (errorEl) {
        errorEl.textContent = error.message || this.dataset.error;
        errorEl.hidden = false;
      }
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${this.root}?section_id=${DVY_CART_SECTION}`);
      if (response.ok) this.render(await response.text());
    } catch (error) {
      console.error('DVY cart: refresh failed', error);
    }
  }

  /** Swaps the drawer content while keeping the open <dialog> element (and focus trap) in place. */
  render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('dvy-cart-drawer');
    if (!fresh) return;
    const content = this.querySelector('[data-dvy-cart-content]');
    const freshContent = fresh.querySelector('[data-dvy-cart-content]');
    if (content && freshContent) content.replaceWith(freshContent);
    this.dataset.cartCount = fresh.dataset.cartCount;
    this.syncCount();
    // Restore focus to an equivalent control, or the close button.
    const controls = [...this.querySelectorAll('[data-dvy-line], [data-dvy-add]')];
    const target = controls[Math.min(this.focusIndex ?? -1, controls.length - 1)] || this.querySelector('[data-dvy-cart-close]');
    this.focusIndex = -1;
    if (this.dialog?.open) target?.focus();
  }

  syncCount() {
    const count = Number(this.dataset.cartCount || 0);
    document.querySelectorAll('[data-dvy-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }
}

if (!customElements.get('dvy-cart-drawer')) customElements.define('dvy-cart-drawer', DvyCartDrawer);
